import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  QuoteAuthor,
  QuoteColumn,
  QuoteFooter,
  QuoteCarouselItem,
  QuoteItemContainer,
  QuoteText,
  QuoteMain,
  QuoteRole,
  AutherDesc,
  QuoteSign,
  QuoteHeader,
  QuoteNav,
  QuoteNavItem
} from './index.style'
import { useInView } from 'react-intersection-observer'
import { animation } from '@styles/vars/animation.style'
import Container from '@components/Container'
import Spacer from '@components/Spacer'
import { Caption } from '@components/TextStyles'
import { convertNewlineToBreak } from '@styles/utils/conversion.style'
import RichText from '@components/RichText'
import { GatsbyImage } from 'gatsby-plugin-image'
import Button from '@components/Button' // ✅ Ensure this path is correct for your project

const Quote = ({ label, content }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isSingleItem, setIsSingleItem] = useState(false)

  const [ref, inView] = useInView({
    rootMargin: animation.rootMargin,
  })

  useEffect(() => {
    setIsSingleItem(content.length === 1)
  }, [content])

  // Navigation Handlers
  const goToPrevious = () => {
    setActiveIndex((prev) => (prev - 1 + content.length) % content.length)
  }

  const goToNext = () => {
    setActiveIndex((prev) => (prev + 1) % content.length)
  }

  const carouselAtEnd = activeIndex === content.length - 1
  const current = content[activeIndex]

  return (
    <QuoteMain ref={ref} className={isSingleItem ? 'oneRotate' : ''}>
      <Container>
        <Spacer size={[30, 70]} />

        {!isSingleItem && (
          <QuoteHeader>
            <QuoteNav>
              <QuoteNavItem>
                <Button
                  variant="outline"
                  onClick={goToPrevious}
                  size="large"
                  color="white"
                  disabled={activeIndex === 0}
                >
                  PREVIOUS
                </Button>
              </QuoteNavItem>
              <QuoteNavItem>
                <Button
                  variant="outline"
                  onClick={goToNext}
                  size="large"
                  color="white"
                  disabled={carouselAtEnd}
                >
                  NEXT
                </Button>
              </QuoteNavItem>
            </QuoteNav>
          </QuoteHeader>
        )}

        <Spacer size={[30, 70]} />

        {label && <><Caption as="h2">{label}</Caption><Spacer size={[30, 50]} /></>}

        <QuoteFooter>
          <QuoteCarouselItem>
            <QuoteItemContainer>
              <QuoteText>
                <QuoteSign>“</QuoteSign>
                <RichText
                  content={current.quoteContent}
                  paragraphSize="small"
                  maxWidth={38}
                  className="quoteContent"
                />
              </QuoteText>

              <QuoteColumn>
                <QuoteAuthor>{current.authorName}</QuoteAuthor>

                {current.authorDescription && (
                  <>
                    <Spacer size={5} />

                    <QuoteRole>
                      <AutherDesc>
                        {convertNewlineToBreak(
                          current.authorDescription.authorDescription
                        )}
                      </AutherDesc>
                      {current.image && (
                        <GatsbyImage
                          image={current.image.gatsbyImageData}
                          alt={current.image.description}
                        />
                      )}
                    </QuoteRole>
                  </>
                )}
              </QuoteColumn>
            </QuoteItemContainer>
          </QuoteCarouselItem>
        </QuoteFooter>

        {/* Dot Navigation */}
        {!isSingleItem && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '30px' }}>
            {content.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: activeIndex === index ? '#000' : '#ccc',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
                aria-label={`Go to quote ${index + 1}`}
              />
            ))}
          </div>
        )}

        <Spacer size={[60, 150]} />
      </Container>
    </QuoteMain>
  )
}

Quote.propTypes = {
  label: PropTypes.string,
  content: PropTypes.array.isRequired,
}

export default Quote
